/**
 * @lines98/client/lib/reconcile-guard — ОБОБЩЁННЫЙ «применить-если-изменилось» guard для
 * ИДЕМПОТЕНТНОГО reconcile гидрированных surface'ов (instant-ui-hydration §3 reconcile).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-playtest 2026-06-10 «повторная перерисовка/переанимация»):
 * снимок (`lib/screen-snapshot`/`meta-snapshot`) рисует динамику МГНОВЕННО, затем приходит
 * server-truth (`getMetaState`/reconcile). Если reconcile БЕЗУСЛОВНО переписывает DOM и
 * рестартит анимации даже когда server-truth == уже показанному (== снимку) — игрок ВИДИТ
 * лишнюю перерисовку: камни вокруг зверя «перерисовываются», полоска уровня переанимируется
 * 0→значение, бейдж уровня моргает. Лекарство: на reconcile сравнить новое значение с уже
 * применённым; равно → **no-op** (DOM не трогаем, анимацию не рестартим). Только реально
 * изменившиеся поля обновляем (attach-не-rerender, code-structure §3).
 *
 * Это ЕДИНЫЙ «ствол» дешёвого guard'а — переиспользуют ВСЕ гидрированные surface'ы
 * (jewelry-card / level-badge / xp-bar / leaderboard viewer-level), а НЕ копируют «помню
 * прошлое значение → сравниваю» по экранам (модель «ствол → ветки», CLAUDE.md / code-
 * structure §1). Две формы:
 *   · `createValueGuard<T>(equals?)` — атомарное скалярное/структурное значение (уровень,
 *     ratio полоски, сигнатура модели карточки): `changed(next)` true только при отличии.
 *   · `signatureOf(...)` — собрать стабильную строку-сигнатуру из примитивов (для guard'а
 *     по «форме» модели без дорогого deep-equal).
 *
 * **Server-truth НЕ нарушается (instant-hydration инвариант):** guard — ЧИСТО UX-оптимизация
 * показа. Он НЕ «залипает»: если server-truth реально другой — `changed` вернёт true и DOM
 * обновится. reconcile по-прежнему перетирает server-truth'ом; guard лишь гасит ХОЛОСТУЮ
 * перерисовку при совпадении. Никакой кэш/guard не вход для проверок/начислений/верификации.
 *
 * Cross-references:
 *   - lib/screen-snapshot.ts / lib/meta-snapshot.ts (снимок — мгновенный показ до reconcile).
 *   - components/jewelry-card.ts (guard модели карточки — камни/кольцо/бейджи/цена).
 *   - components/level-badge.ts (guard уровня/титула — бейдж не моргает).
 *   - screens/profile-screen.ts (guard ratio XP-бара — анимация только при дельте).
 *   - screens/leaderboard-screen.ts (guard viewerLevel — body не перерисовывается зря).
 *   - .claude/skills/instant-ui-hydration/SKILL.md §3 (reconcile без скачка).
 */

/**
 * Guard «применить-если-изменилось» для одного логического значения `T`.
 * Хранит последнее ПРИНЯТОЕ значение; `changed(next)` возвращает `true` ТОЛЬКО когда
 * `next` отличается от него (по `equals`, по умолчанию `Object.is`) — и тогда же
 * запоминает `next` как новое «показанное». При совпадении — `false`, вызывающий делает
 * no-op (не трогает DOM / не рестартит анимацию).
 *
 * Первый вызов всегда `true` (ничего ещё не показано) — это «первый рендер из снимка».
 */
export interface ValueGuard<T> {
  /** Изменилось ли значение vs последнего принятого? Принимает `next` как показанное при true. */
  changed(next: T): boolean;
  /** Последнее принятое значение (или `undefined` до первого `changed`). Для отладки/тестов. */
  readonly last: T | undefined;
  /** Сбросить память (напр. смена игрока) — следующий `changed` снова вернёт `true`. */
  reset(): void;
}

/**
 * Создать guard для значения `T`. `equals` — кастомное сравнение (по умолчанию `Object.is`,
 * годится для чисел/строк/булей/сигнатур). Для «формы» модели подавай строку-сигнатуру
 * (`signatureOf(...)`) — дёшево и без deep-equal.
 */
export function createValueGuard<T>(equals: (a: T, b: T) => boolean = Object.is): ValueGuard<T> {
  let has = false;
  let prev: T | undefined;
  return {
    changed(next: T): boolean {
      if (has && equals(prev as T, next)) return false;
      has = true;
      prev = next;
      return true;
    },
    get last(): T | undefined {
      return prev;
    },
    reset(): void {
      has = false;
      prev = undefined;
    },
  };
}

/**
 * Собрать стабильную строку-сигнатуру из примитивов (числа/строки/були/null/undefined).
 * Для guard'а по «форме» модели (карточка зверя: уровень + маска радуги + цена + …) без
 * дорогого структурного сравнения: одинаковые поля → одинаковая строка → `changed` false.
 * Разделитель `` (unit separator) не встречается в данных → нет коллизий склейки.
 */
export function signatureOf(...parts: ReadonlyArray<string | number | boolean | null | undefined>): string {
  return parts.map((p) => (p === undefined ? '' : String(p))).join('');
}
